import { debounce, isEqual, defaultsDeep } from 'lodash'
import { requestCommon } from 'live/lib/utils'
import Subscriber from 'live/services/subscription'

export type MarkerPoints = {
    name: string
    color: string
    value: number
    unit: string
    show?: boolean
    index?: number
}

export interface MarkersAnnotation {
    uid?: string
    begin: number
    end: number | null
    dashboardSpan: string
    dashboardId: number
    dashboard?: string
    createdAt?: number
    author?: string
    message?: string
    dashboardFilters: Record<string, unknown>
    __color: string
    __src: string
    __delete?: string[]
    extra: {
        target: { spanType: string; widgetId?: number }
        value: number
        unit?: string
        id: string
        widgets: {
            widgetId: number
            widgetName: string
            points: MarkerPoints[]
        }[]
    }
    markerType: 'PLOT_VALUE' | 'STATIC'
    rawExtra?: Record<string, unknown>
}

/**
 * Annotation record event
 */
export type MarkersData = {
    annotationId?: string
    message?: string
    originalValue?: number
    originalUnit?: number
    markerType?: 'PLOT_VALUE' | 'STATIC'
    __rawExtra?: MarkersAnnotation['extra']
} & MarkersAnnotation['extra']

export interface QueryParams {
    dashboardId: number
    widgetId: number
    span: string
    spanType: string
    widgetType: string
    markerType?: 'PLOT_VALUE' | 'STATIC'
    dashboardSpan: Record<string, string>
    spanConfig: Record<string, unknown>
    globalRealtime?: boolean
    lookupValues?: Record<string, string[]>
    extra?: Record<string, unknown>
}

// +  = A query was added (request)
// -  = A query was removed (close)
// +- = A query was updated (request, close)
// '' = No action

type ChangeType = '+' | '-' | '+-' | ''
export class ObservableDebouncedSet<T> {
    // @TODO: convert this back to a set and stop depending on index
    private internalSet = new Array<[ChangeType, T]>() // Set is not indexable
    private wait: number
    private subscribers: ((items: Array<[ChangeType, T]>) => void)[] = []
    private compare: (a: T, b: T) => boolean
    private makeId: (a: T) => string | number

    constructor(wait: number, makeId: (a: T) => string | number, compare: (a: T, b: T) => boolean) {
        this.wait = wait
        this.compare = compare
        this.makeId = makeId
        this.emit = debounce(this.emit.bind(this), this.wait)
    }

    private emit(): void {
        const changes = this.internalSet.map(a => [a[0], a[1]]) as [ChangeType, T][]
        this.subscribers.forEach(fn => fn(changes))
        this.internalSet.forEach(item => (item[0] = ''))
    }

    subscribe(callback: (items: Array<[ChangeType, T]>) => void): void {
        this.subscribers.push(callback)
    }

    add(newItem: T): void {
        const [oldItem, index] = this.find(newItem)

        if (oldItem != null && this.compare(oldItem, newItem)) return
        else if (oldItem != null && index != null) this.internalSet[index] = ['+-', newItem]
        else this.internalSet.push(['+', newItem])

        this.emit()
    }

    remove(item: T): void {
        const [item2, index] = this.find(item)

        if (!item2 || index == null) return

        // Ideally this sould remove the item from the set
        this.internalSet[index][0] = '-'
        this.internalSet[index][1] = null
        this.emit()
    }

    find(item: T): [T, number] | [] {
        for (let i = 0; i < this.internalSet.length; i++) {
            const attempt = this.internalSet[i][1]

            if (attempt != null && this.makeId(item) === this.makeId(attempt)) {
                return [attempt, i]
            }
        }

        return []
    }

    clearItems(): void {
        this.internalSet = []
    }

    getItems(): [ChangeType, T][] {
        return Array.from(this.internalSet)
    }
}
class MarkersQuerier {
    url = '/services/plugin-annotations/markers-query/'
    subscriber: Subscriber

    constructor(subscriber?: Subscriber) {
        if (!subscriber) {
            subscriber = new Subscriber({ url: this.url })
        }

        this.subscriber = subscriber
    }

    request(param: QueryParams, index?: number): any {
        return this.subscriber.request(param, index)
    }

    close(index?: number): void {
        this.subscriber.close(index)
    }

    flush(indexes?: number | number[]): void {
        this.subscriber.flush(indexes)
    }
}

export default class ApiMarkers {
    private listenEventsWith: {
        onEvent: (event: MarkersData) => void
        onDelete: (event: MarkersData) => void
    } | null = null

    private markersQuerier: MarkersQuerier

    private saveMarkerStrategies: Record<string, (marker: MarkersAnnotation) => Promise<MarkersAnnotation | Error>> = {}

    private globalQuery: QueryParams = null

    public activeQueries: ObservableDebouncedSet<QueryParams>

    // @visibleForTesting
    constructor(subscriber?: Subscriber, wait = 300) {
        this.markersQuerier = new MarkersQuerier(subscriber)

        this.activeQueries = new ObservableDebouncedSet<QueryParams>(wait, q => q.widgetId, (a, b) => {
                const sameWidget = a.widgetId === b.widgetId
                const sameSpan = a.span === b.span
                const sameDashboardFilter = isEqual(a.lookupValues, b.lookupValues)
                const sameExtra = isEqual(a.extra, b.extra)

                return sameWidget && sameSpan && sameDashboardFilter && sameExtra
            }
        )

        this.activeQueries.subscribe(this.updateQuery)
    }

    private updateQuery = (widgets: Array<[ChangeType, QueryParams]>): void => {
        // Valid changes have one of: [ChangeType, QueryParams] as non nullish
        const validChanges = widgets.filter(([changeType, params]) => changeType || params)

        // close all - leaving the dashboard - considering only valid changes
        if (validChanges.every(item => item[0] === '-')) return this.destroy()

        // add all - initial query
        if (widgets.every(item => item[0] === '+')) {
            // The first query is the global realtime.
            // span, spanType and widgetId are totally
            // ignored when globalRealtime is set to true.
            this.shouldUpdateRealtimeQuery(widgets)

            this.markersQuerier.request({ ...this.globalQuery, globalRealtime: true }).on('event', this.onEvent)

            for (const [, param] of widgets) {
                this.markersQuerier.request(param).on('event', this.onEvent)
            }

            return this.markersQuerier.flush()
        }

        // Start and stop of individual queries due to span changes.
        // The `for loop` counter starts at '1' because '0' is the
        // globalRealtime query.
        const willFlush = []
        for (let index = 1; index <= widgets.length; index++) {
            const [change, param] = widgets[index - 1]

            if (change === '+') {
                this.markersQuerier.request(param).on('event', this.onEvent)
            } else if (change === '-') {
                this.markersQuerier.close(index)
            } else if (change === '+-') {
                this.markersQuerier.close(index)
                this.markersQuerier.request(param, index).on('event', this.onEvent)
            }

            if (change.includes('+')) willFlush.push(index)
        }

        // Test if realtime query has changed
        if (this.shouldUpdateRealtimeQuery(widgets)) {
            this.markersQuerier.close(0)
            this.markersQuerier.request({ ...this.globalQuery, globalRealtime: true }, 0).on('event', this.onEvent)

            willFlush.push(0)
        }

        // There should be an way to flush all specified
        // requests at once. The same behavior you get
        // when the argument is undefined.
        this.markersQuerier.flush(willFlush)
    }

    private shouldUpdateRealtimeQuery(widgets: Array<[ChangeType, QueryParams]>): boolean {
        // Will reduce the array of queries in `widgets` to one query per span type.
        // Hence, if there are 10 items in `widgets`: 5 with span type "time" and another
        // 5 span type "custom" the result would be a object with 2 keys: "time" and "custom".
        const globalRealtimeMap: Record<string, QueryParams> = widgets.reduce((acc, [, el]) => {
            if (el?.spanType == null) return acc

            acc[el.spanType] = defaultsDeep(acc[el.spanType] || { globalRealtime: true }, el)

            return acc
        }, {})

        const globalQuery = Object.values(globalRealtimeMap)[0]

        const isInitial = this.globalQuery == null

        const sameDashboardSpan = isEqual(this.globalQuery?.dashboardSpan, globalQuery.dashboardSpan)
        const sameLookupValues = isEqual(this.globalQuery?.lookupValues, globalQuery.lookupValues)
        const sameExtra = isEqual(this.globalQuery?.extra, globalQuery.extra)

        this.globalQuery = globalQuery

        return isInitial || !sameDashboardSpan || !sameLookupValues || !sameExtra
    }

    private onEvent = (annotations: MarkersAnnotation[]): void => {
        for (const annotation of annotations) {
            if (annotation.__delete && this.listenEventsWith != null) {
                this.listenEventsWith.onDelete(this.toMarkerData(annotation))
            } else if (this.listenEventsWith != null) {
                this.listenEventsWith.onEvent(this.toMarkerData(annotation))
            }
        }
    }

    public toMarkerData(annotation: MarkersAnnotation): MarkersData {
        annotation.extra.value = annotation.begin

        return {
            ...annotation.extra,
            annotationId: annotation.uid,
            message: annotation.message,
            markerType: annotation.markerType,
            __rawExtra: (annotation.rawExtra as unknown) as MarkersAnnotation['extra']
        }
    }

    public save = async (marker: MarkersData): Promise<MarkersData | void> => {
        const aux = this.activeQueries.getItems().find(([, a]) => {
            return a?.dashboardId != null
        })
        const { dashboardId, dashboardSpan, lookupValues } = aux![1]

        const data = {
            uid: marker.annotationId,
            begin: marker.value,
            dashboardFilters: lookupValues || {},
            dashboardId: dashboardId,
            dashboardSpan: dashboardSpan?.span,
            end: null,
            message: marker.message || null,
            __color: '#37a3ef',
            __src: 'markers',
            extra: {
                id: marker.id,
                widgets: marker.widgets,
                target: marker.target,
                value: marker.value,
                unit: marker.unit
            }
        } as MarkersAnnotation


        // Make sure its a long!
        data.begin = Math.round(data.begin)
        data.extra.value = data.begin

        return fetch('/services/plugin-annotations/annotations/', {
            method: 'POST',
            ...requestCommon,
            body: JSON.stringify(data)
        })
            .then(resp => {
                if (!resp.ok) throw new Error(resp.statusText)

                return resp.json()
            })
            .then(item => {
                // To be properly persisted (be a long) the value might have changed.
                item.begin = marker.originalValue || item.begin
                item.extra.unit = marker.originalUnit || item.extra.unit
                item.extra.value = item.begin
                item.markerType = marker.markerType

                return this.toMarkerData(item)
            })
            .catch(err => console.error('Error saving annotation, ', err))
    }

    public remove = async (marker: MarkersData): Promise<Error | void> => {
        return fetch('/services/plugin-annotations/annotations/' + marker.annotationId, {
            method: 'DELETE',
            ...requestCommon
        }).then(r => (!r.ok ? new Error(r.statusText) : undefined))
    }

    public setListener(listener: {
        onEvent: (event: MarkersData) => void
        onDelete: (event: MarkersData) => void
    }): void {
        this.listenEventsWith = listener
    }

    public registerSaveMarkerStrategyFor(
        spanType: string,
        saveFn: (marker: MarkersAnnotation) => Promise<MarkersAnnotation | Error>
    ): void {
        if (!this.saveMarkerStrategies[spanType]) {
            this.saveMarkerStrategies[spanType] = saveFn
        } else {
            console.warn('An strategy to save ' + spanType + ' markers already exist!')
        }
    }

    public destroy(): void {
        this.markersQuerier.close()
        this.activeQueries.clearItems()
        this.dashboardSpan = undefined
    }
}
